import {
  EmailAuthProvider,
  GoogleAuthProvider,
  applyActionCode as firebaseApplyActionCode,
  confirmPasswordReset as firebaseConfirmPasswordReset,
  reauthenticateWithCredential,
  sendEmailVerification as firebaseSendEmailVerification,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
  sendSignInLinkToEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  updatePassword as firebaseUpdatePassword,
  verifyPasswordResetCode as firebaseVerifyPasswordResetCode,
} from 'firebase/auth';
import type { AdminUserSession } from './auth-session.js';
import { getFirebaseClients } from './firebase.js';
import { ADMIN_STRINGS } from './strings.js';

export const EMAIL_FOR_SIGN_IN_STORAGE_KEY = 'mp_admin_email_for_sign_in';

export interface AuthActionHandlersConfig {
  readonly isControlled: boolean;
  readonly session: AdminUserSession | null;
  readonly onMarkEmailVerified: () => void;
  readonly onMarkVerificationEmailSent: (sent: boolean) => void;
  readonly onMarkEmailLinkSent: (sent: boolean) => void;
  readonly onMarkPasswordResetSent: (sent: boolean) => void;
  readonly onClearError: () => void;
  readonly onSignInWithGoogle?: (() => Promise<void>) | undefined;
  readonly onSignInWithPassword?: ((email: string, password: string) => Promise<void>) | undefined;
  readonly onSendPasswordReset?: ((email: string) => Promise<void>) | undefined;
  readonly onSendEmailLink?: ((email: string) => Promise<void>) | undefined;
  readonly onChangePassword?:
    ((currentPassword: string, newPassword: string) => Promise<void>) | undefined;
  readonly onVerifyResetCode?: ((oobCode: string) => Promise<string>) | undefined;
  readonly onConfirmPasswordReset?:
    ((oobCode: string, newPassword: string) => Promise<void>) | undefined;
  readonly onApplyEmailVerificationCode?: ((oobCode: string) => Promise<void>) | undefined;
  readonly onSendVerificationEmail?: (() => Promise<void>) | undefined;
}

export function createFirebaseAuthActionHandlers(config: AuthActionHandlersConfig) {
  const signInWithGoogle = async (): Promise<void> => {
    config.onClearError();
    if (config.onSignInWithGoogle) {
      await config.onSignInWithGoogle();
      return;
    }
    const { auth } = getFirebaseClients();
    const provider = new GoogleAuthProvider();
    await signInWithPopup(auth, provider);
  };

  const sendEmailLink = async (email: string): Promise<void> => {
    config.onClearError();
    config.onMarkEmailLinkSent(false);
    config.onMarkPasswordResetSent(false);
    const trimmedEmail = email.trim();
    if (trimmedEmail.length === 0) {
      throw new Error('Please enter your work email address first.');
    }
    if (config.onSendEmailLink) {
      await config.onSendEmailLink(trimmedEmail);
      config.onMarkEmailLinkSent(true);
      return;
    }
    const { auth } = getFirebaseClients();
    const actionCodeSettings = {
      url: typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5173',
      handleCodeInApp: true,
    };
    await sendSignInLinkToEmail(auth, trimmedEmail, actionCodeSettings);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(EMAIL_FOR_SIGN_IN_STORAGE_KEY, trimmedEmail);
    }
    config.onMarkEmailLinkSent(true);
  };

  const sendPasswordReset = async (email: string): Promise<void> => {
    config.onClearError();
    config.onMarkPasswordResetSent(false);
    config.onMarkEmailLinkSent(false);
    const trimmedEmail = email.trim();
    if (trimmedEmail.length === 0) {
      throw new Error('Please enter your work email address first.');
    }
    if (config.onSendPasswordReset) {
      await config.onSendPasswordReset(trimmedEmail);
      config.onMarkPasswordResetSent(true);
      return;
    }
    const { auth } = getFirebaseClients();
    await firebaseSendPasswordResetEmail(auth, trimmedEmail);
    config.onMarkPasswordResetSent(true);
  };

  const signInWithPassword = async (email: string, password: string): Promise<void> => {
    config.onClearError();
    const trimmedEmail = email.trim();
    if (trimmedEmail.length === 0) {
      throw new Error('Please enter your work email address first.');
    }
    if (password.length === 0) {
      throw new Error('Please enter your password.');
    }
    if (config.onSignInWithPassword) {
      await config.onSignInWithPassword(trimmedEmail, password);
      return;
    }
    const { auth } = getFirebaseClients();
    await signInWithEmailAndPassword(auth, trimmedEmail, password);
  };
  const changePassword = async (currentPassword: string, newPassword: string): Promise<void> => {
    config.onClearError();
    if (newPassword.trim().length < 8) {
      throw new Error('Please choose a new password with at least 8 characters.');
    }
    if (config.onChangePassword) {
      await config.onChangePassword(currentPassword, newPassword);
      return;
    }
    if (config.isControlled) {
      if (currentPassword.trim().length === 0) {
        throw new Error('Please enter your current password.');
      }
      return;
    }
    const { auth } = getFirebaseClients();
    const user = auth.currentUser;
    if (!user) {
      throw new Error(
        'Your session has expired. Please sign in again before changing your password.',
      );
    }
    if (currentPassword.trim().length > 0 && user.email) {
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
    }
    await firebaseUpdatePassword(user, newPassword);
  };

  const verifyResetCode = async (oobCode: string): Promise<string> => {
    config.onClearError();
    const trimmedCode = oobCode.trim();
    if (trimmedCode.length === 0) {
      throw new Error(ADMIN_STRINGS.errors.resetPasswordActionFallback);
    }
    if (config.onVerifyResetCode) {
      return config.onVerifyResetCode(trimmedCode);
    }
    if (config.isControlled) {
      return config.session?.email ?? 'admin@myplayr.co.uk';
    }
    const { auth } = getFirebaseClients();
    return firebaseVerifyPasswordResetCode(auth, trimmedCode);
  };

  const confirmPasswordResetAction = async (
    oobCode: string,
    newPassword: string,
  ): Promise<void> => {
    config.onClearError();
    if (newPassword.trim().length < 8) {
      throw new Error('Please choose a new password with at least 8 characters.');
    }
    const trimmedCode = oobCode.trim();
    if (trimmedCode.length === 0) {
      throw new Error(ADMIN_STRINGS.errors.resetPasswordActionFallback);
    }
    if (config.onConfirmPasswordReset) {
      await config.onConfirmPasswordReset(trimmedCode, newPassword);
      return;
    }
    if (config.isControlled) {
      return;
    }
    const { auth } = getFirebaseClients();
    await firebaseConfirmPasswordReset(auth, trimmedCode, newPassword);
  };

  const applyEmailVerificationCode = async (oobCode: string): Promise<void> => {
    config.onClearError();
    const trimmedCode = oobCode.trim();
    if (trimmedCode.length === 0) {
      throw new Error(ADMIN_STRINGS.errors.verifyEmailActionFallback);
    }
    if (config.onApplyEmailVerificationCode) {
      await config.onApplyEmailVerificationCode(trimmedCode);
      config.onMarkEmailVerified();
      return;
    }
    if (config.isControlled) {
      config.onMarkEmailVerified();
      return;
    }
    const { auth } = getFirebaseClients();
    await firebaseApplyActionCode(auth, trimmedCode);
    if (auth.currentUser) {
      await auth.currentUser.reload();
    }
    config.onMarkEmailVerified();
  };

  const sendVerificationEmail = async (): Promise<void> => {
    config.onClearError();
    config.onMarkVerificationEmailSent(false);
    if (config.onSendVerificationEmail) {
      await config.onSendVerificationEmail();
      config.onMarkVerificationEmailSent(true);
      return;
    }
    if (config.isControlled) {
      config.onMarkVerificationEmailSent(true);
      return;
    }
    const { auth } = getFirebaseClients();
    const user = auth.currentUser;
    if (!user) {
      throw new Error('Please sign in first so we know where to send your verification email.');
    }
    const actionCodeSettings = {
      url:
        typeof window !== 'undefined'
          ? `${window.location.origin}/auth/action?mode=verifyEmail`
          : 'http://localhost:5173/auth/action?mode=verifyEmail',
      handleCodeInApp: false,
    };
    await firebaseSendEmailVerification(user, actionCodeSettings);
    config.onMarkVerificationEmailSent(true);
  };

  return {
    signInWithGoogle,
    sendEmailLink,
    sendPasswordReset,
    signInWithPassword,
    changePassword,
    verifyResetCode,
    confirmPasswordResetAction,
    applyEmailVerificationCode,
    sendVerificationEmail,
  };
}
